import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import type { FbcChannelId } from './future-chat/fbc-design-grammar.js'
import {
  FbcDot,
  FbcSignalLine,
  FbcStatusLabel,
} from './future-chat/FbcGrammar.js'

export function FixerSection({
  id,
  children,
  className = '',
}: {
  id?: string
  children: ReactNode
  className?: string
}) {
  return (
    <section id={id} className={`fixer-section ${className}`}>
      <div className="fixer-section-inner">{children}</div>
    </section>
  )
}

export function FixerKicker({
  children,
  channel = 'phase',
  active = true,
  className = '',
}: {
  children: ReactNode
  channel?: FbcChannelId
  active?: boolean
  className?: string
}) {
  return (
    <FbcStatusLabel
      channel={channel}
      state={active ? 'signal' : 'idle'}
      trailing
      className={`fixer-kicker-label ${className}`}
    >
      {children}
    </FbcStatusLabel>
  )
}

export function FixerSectionIntro({
  kicker,
  title,
  copy,
  titleId,
  channel = 'phase',
}: {
  kicker: string
  title: string
  copy?: string | undefined
  titleId?: string | undefined
  channel?: FbcChannelId
}) {
  return (
    <div className="fixer-stack-md max-w-2xl">
      {kicker ? <FixerKicker channel={channel}>{kicker}</FixerKicker> : null}
      <h2 id={titleId} className="fixer-heading">{title}</h2>
      {copy ? <p className="fixer-body">{copy}</p> : null}
      <FbcSignalLine variant="short" active className="fixer-section-signal max-w-[140px]" />
    </div>
  )
}

export function FixerSignalDivider({ className = '' }: { className?: string }) {
  return (
    <div className={`fixer-signal-divider ${className}`} aria-hidden="true">
      <FbcSignalLine variant="full" active />
    </div>
  )
}

export function FixerCard({
  children,
  className = '',
  interactive = false,
  signalRail = true,
  ...rest
}: {
  children: ReactNode
  className?: string
  interactive?: boolean
  signalRail?: boolean
} & ComponentPropsWithoutRef<'article'>) {
  return (
    <article
      className={`fixer-glass-card ${interactive ? 'fixer-glass-card-interactive' : ''} ${className}`}
      {...rest}
    >
      {signalRail ? (
        <div className="fixer-card-signal-rail" aria-hidden="true">
          <FbcSignalLine variant="baseline" active />
        </div>
      ) : null}
      {children}
    </article>
  )
}

export function FixerBulletList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {items.map(item => (
        <li key={item} className="flex items-start gap-2.5 fixer-body-sm">
          <FbcDot state="signal" className="mt-[7px]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export function FixerBriefRow({ children }: { children: ReactNode }) {
  return (
    <div className="fixer-brief-row">
      <span className="fixer-brief-rail" aria-hidden="true">
        <FbcDot state="active" />
        <span className="fixer-brief-rail-line" />
        <FbcDot state="signal" />
      </span>
      <span className="fixer-brief-row-text">{children}</span>
    </div>
  )
}

export function FixerCtaRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      {children}
    </div>
  )
}

export interface FixerAccordionItem {
  id: string
  title: string
  summary?: string
  children: ReactNode
}

export function FixerAccordion({ items }: { items: FixerAccordionItem[] }) {
  return (
    <div className="fixer-accordion" role="list">
      {items.map(item => (
        <details key={item.id} className="fixer-accordion-item" role="listitem">
          <summary className="fixer-accordion-summary">
            <span className="fixer-accordion-marker" aria-hidden="true">
              <FbcDot state="signal" />
            </span>
            <span className="fixer-accordion-title-wrap">
              <span className="fixer-subheading">{item.title}</span>
              {item.summary ? <span className="fixer-body-sm fixer-accordion-summary-text">{item.summary}</span> : null}
            </span>
            <span className="fixer-accordion-chevron" aria-hidden="true" />
          </summary>
          <div className="fixer-accordion-body">{item.children}</div>
        </details>
      ))}
    </div>
  )
}
